/**
 * CITATIONS registry — every legal claim a user can see, WITH a verification status.
 * Loads schema/citations.json (the single canonical source per ADR-0017) and
 * Zod-validates it at module load. FAIL CLOSED: any invalid entry throws — the
 * registry never degrades to a partial load.
 *
 * The statuses mean things (CP-R1315 canon):
 *   verified   — primary source pulled + quoted
 *   pending    — FOLLOW IT, DO NOT CITE IT AS LAW
 *   practice   — professional convention, NOT law
 *   conflict   — the rule and the law disagree
 *   unreviewed — claims NOTHING
 *
 * 'verified' without a resolvable source is UNREPRESENTABLE: the schema refines
 * verified ⇒ non-empty url, pending ⇒ pendingNote, conflict ⇒ conflictNote.
 */
import { z } from 'zod'
import { AppError } from '../errors/app-error'
import citationsJson from '../../../../schema/citations.json'

export const CITATION_STATUSES = ['verified', 'pending', 'practice', 'conflict', 'unreviewed'] as const
export const CitationStatusSchema = z.enum(CITATION_STATUSES)
export type CitationStatus = z.infer<typeof CitationStatusSchema>

export const CitationEntrySchema = z
  .object({
    id: z.string().min(1),
    ref: z.string().min(1),
    status: CitationStatusSchema,
    purpose: z.string().min(1),
    url: z.string(),
    pendingNote: z.string().min(1).optional(),
    conflictNote: z.string().min(1).optional(),
    noDayCount: z.boolean().optional(),
  })
  .strict()
  .superRefine((c, ctx) => {
    if (c.status === 'verified' && c.url.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `citation '${c.id}': status 'verified' requires a non-empty primary-source url — verified-without-source is unrepresentable`,
      })
    }
    if (c.status === 'pending' && !c.pendingNote) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `citation '${c.id}': status 'pending' requires a pendingNote saying what is not yet law`,
      })
    }
    if (c.status === 'conflict' && !c.conflictNote) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `citation '${c.id}': status 'conflict' requires a conflictNote saying which authority to follow`,
      })
    }
  })

export type CitationEntry = z.infer<typeof CitationEntrySchema>

const CitationsFileSchema = z
  .object({
    description: z.string(),
    version: z.string(),
    target: z.string(),
    statuses: z.record(z.string()),
    citations: z.array(CitationEntrySchema).min(20),
  })
  .strict()

function loadCitations(): readonly CitationEntry[] {
  const parsed = CitationsFileSchema.safeParse(citationsJson)
  if (!parsed.success) {
    throw AppError.validation(
      'CITATIONS registry failed validation — refusing to load a partial or malformed legal registry (fail closed)',
      parsed.error.issues,
    )
  }
  const declaredStatuses = Object.keys(parsed.data.statuses).sort()
  const canonical = [...CITATION_STATUSES].sort()
  if (JSON.stringify(declaredStatuses) !== JSON.stringify(canonical)) {
    throw AppError.validation(
      `CITATIONS registry status vocabulary drifted: file declares [${declaredStatuses.join(', ')}], canon is [${canonical.join(', ')}]`,
    )
  }
  const seen = new Set<string>()
  for (const c of parsed.data.citations) {
    if (seen.has(c.id)) {
      throw AppError.validation(`CITATIONS registry: duplicate citation id '${c.id}'`)
    }
    seen.add(c.id)
  }
  return Object.freeze(parsed.data.citations)
}

/** Every citation, validated. Frozen — the registry is read-only at runtime. */
export const CITATIONS: readonly CitationEntry[] = loadCitations()

const CITATION_INDEX: ReadonlyMap<string, CitationEntry> = new Map(CITATIONS.map((c) => [c.id, c]))

export function hasCitation(id: string): boolean {
  return CITATION_INDEX.has(id)
}

/** Resolve a citation by id. THROWS on unknown id — an unresolvable cite is a build defect, not a blank. */
export function getCitation(id: string): CitationEntry {
  const c = CITATION_INDEX.get(id)
  if (!c) {
    throw AppError.notFound(
      `Unknown citation id '${id}' — legal claims resolve from the CITATIONS registry only (Rule 1); an unresolvable cite is a build defect`,
    )
  }
  return c
}

/** The user-facing reference string for a citation id (the `_citeRef1315` analogue). */
export function citeRef(id: string): string {
  return getCitation(id).ref
}

/** The verification status for a citation id. 'pending' means FOLLOW IT, DO NOT CITE IT AS LAW. */
export function citationStatus(id: string): CitationStatus {
  return getCitation(id).status
}
